<template>
  <AppSidebar
    v-model="drawerIsOpen"
    :mini="computedMini"
    :permanent="permanent"
  >
    <ProjectSidebarLayout :items="projectSections" />
  </AppSidebar>
</template>

<script>
import { defineAsyncComponent, nextTick } from 'vue'
import { mapState, mapGetters, mapMutations } from 'vuex'

import AppSidebar from '@/common/app-sidebar/AppSidebar.vue'
import { projectPages } from '@/common/navigation-drawer/roomNavigationData.js'
import { toKebabCase } from '@/common/utils/strings.js'
import ProjectSidebarLayout from '@/project/sidebar/ProjectSidebarLayout.vue'
import {
  QUESTION_ANSWER_ROUTE_NAME,
  ROOM_BIBLES_ROUTE_NAME,
  ROOM_CHECKLIST_ROUTE_NAME,
  ROOM_DOCUMENTS_ROUTE_NAME,
  ROOM_LOGS_ROUTE_NAME,
  ROOM_MEMBERS_ROUTE_NAME,
  ROOM_SETTINGS_ROUTE_NAME,
  ROOM_SIGNED_DOCUMENTS_ROUTE_NAME,
  SIGNING_CHECKLIST_ROUTE_NAME,
} from '@/router/routerData'
import { GlobalMutationTypes } from '@/store/modules/global/mutation_types'

// This duration is meant to wait after any drawer width change so that
// we can trigger a refresh to update the v-content position relative to the new drawer width
// This is due to a Vuetify bug when the navigation drawer has multiple width values set to it
// Especially when using different units
const CONTENT_REFLOW_DELAY = 190
const TRANSLATION_KEY_PREFIX = 'project.ProjectSidebar'

export default {
  name: 'ProjectSidebar',
  components: {
    AppSidebar,
    ProjectSidebarLayout,
  },
  data () {
    return {
      permanent: false,
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom']),
    ...mapState('global', ['mini', 'toggleProjectDrawer']),
    ...mapGetters('user', ['isProtegeAskEnabled']),
    drawerIsOpen: {
      get () {
        return this.$vuetify.display.mobile
          ? this.toggleProjectDrawer
          : true
      },
      set (val) {
        this.SET_TOGGLE_PROJECT_DRAWER(val)
      },
    },
    aiAssistantMenuElement () {
      return {
        customComponent: defineAsyncComponent(() => import('@/common/ai-assistant/AIAssistantNavItem.vue')),
      }
    },
    membersMenuElement () {
      return this.getProjectSection(ROOM_MEMBERS_ROUTE_NAME)
    },
    documentsMenuElement () {
      return this.getProjectSection(ROOM_DOCUMENTS_ROUTE_NAME)
    },
    checkListMenuElement () {
      return this.getProjectSection(ROOM_CHECKLIST_ROUTE_NAME)
    },
    signingChecklistMenuElement () {
      return this.getProjectSection(SIGNING_CHECKLIST_ROUTE_NAME)
    },
    signedDocumentsMenuElement () {
      return this.getProjectSection(ROOM_SIGNED_DOCUMENTS_ROUTE_NAME)
    },
    bibleMenuElement () {
      return this.getProjectSection(ROOM_BIBLES_ROUTE_NAME)
    },
    settingsMenuElement () {
      return this.getProjectSection(ROOM_SETTINGS_ROUTE_NAME)
    },
    questionAnswersListMenuElement () {
      return this.getProjectSection(QUESTION_ANSWER_ROUTE_NAME)
    },
    logsMenuElement () {
      return this.getProjectSection(ROOM_LOGS_ROUTE_NAME)
    },
    projectSections () {
      if (this.currentRoom?.subscriptionDisabledFor) {
        return []
      }

      const menu = []
      if (this.currentRoom?.isDataroom) {
        // No need to check for room access, if the prop is truthy, we have a room
        menu.push(this.membersMenuElement)
        if (!this.currentRoom.hideDocuments) {
          menu.push(this.documentsMenuElement)
        }
        menu.push(this.questionAnswersListMenuElement)
        if (this.currentRoom.isPM) {
          menu.push(this.logsMenuElement)
        }
        menu.push(this.bibleMenuElement)

        if(this.isProtegeAskEnabled) {
          menu.push(this.aiAssistantMenuElement)
        }
        if (this.currentRoom.isPM) {
          menu.push(this.settingsMenuElement)
        }
      } else if (this.currentRoom) {
        menu.push(this.membersMenuElement)
        if (!this.currentRoom.hideDocuments) {
          menu.push(this.documentsMenuElement)
        }
        if (!this.currentRoom.hideTodo) {
          menu.push(this.checkListMenuElement)
        }

        menu.push(this.signingChecklistMenuElement, this.signedDocumentsMenuElement, this.bibleMenuElement)

        if(this.isProtegeAskEnabled) {
          menu.push(this.aiAssistantMenuElement)
        }
        if (this.currentRoom.isPM) {
          menu.push(this.settingsMenuElement)
        }
      }
      return menu
    },
    computedMini: {
      get () {
        return this.mini
      },
      set (val) {
        this.$store.commit(`global/${GlobalMutationTypes.SET_MINI}`, val)
      },
    },
  },
  watch: {
    '$vuetify.display.mobile': {
      async handler (newValue) {
        await nextTick()

        setTimeout(() => {
          this.permanent = !newValue
          if (newValue) {
            this.computedMini = false
          }
        }, CONTENT_REFLOW_DELAY)
      },
      immediate: true,
    },
    computedMini: {
      async handler () {
        if (!this.$vuetify.display.mobile) {
          await nextTick()
          setTimeout(() => {
            this.permanent = !this.permanent
          }, CONTENT_REFLOW_DELAY)
          this.permanent = !this.permanent
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations('global', [GlobalMutationTypes.SET_TOGGLE_PROJECT_DRAWER]),
    getProjectSection(routeName) {
      const sectionData = projectPages[routeName]
      return {
        key: routeName,
        title: this.$t(`${TRANSLATION_KEY_PREFIX}.${sectionData.translationKey}`),
        icon: sectionData.icon,
        locatorValue: toKebabCase(sectionData.translationKey) + '-nav-item',
      }
    },
  },
}
</script>
